// src/components/private/LoggedInNavBar.js
// Modern vertical navigation bar for Saarthi

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { logoutUser, resetAuthError } from '../../auth/authActions';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './LoggedInNavBar.css';

function LoggedInNavBar() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const sidebarRef = useRef(null);
    const userInfo = useSelector(state => state.auth.userInfo);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setActiveDropdown(null);
                if (window.innerWidth <= 768) {
                    setIsMobileMenuOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
    }, [location.pathname]);

    const handleLogout = () => {
        dispatch(logoutUser());
        dispatch(resetAuthError());
        navigate('/home');
    };

    const toggleDropdown = (dropdownName) => {
        setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const isActive = (path) => {
        return location.pathname.startsWith(path);
    };

    const getUserInitials = () => {
        if (userInfo && userInfo.username) {
            return userInfo.username.substring(0, 2).toUpperCase();
        }
        return 'SA';
    };

    return (
        <>
            {/* Mobile Toggle Button */}
            <button className="saarthi-toggle-btn" onClick={toggleMobileMenu}>
                <span className="material-icons">
                    {isMobileMenuOpen ? 'close' : 'menu'}
                </span>
            </button>

            {/* Sidebar */}
            <div 
                ref={sidebarRef}
                className={`saarthi-sidebar ${isMobileMenuOpen ? 'active' : ''}`}
            >
                {/* Mobile Dashboard Button */}
                <div className="saarthi-mobile-dashboard d-md-none">
                    <Link to="/dashboard" className="btn">
                        <span className="material-icons">dashboard</span>
                        Dashboard
                    </Link>
                </div>

                {/* Logo */}
                <div className="saarthi-logo-wrapper">
                    <Link to="/dashboard" className="saarthi-logo-link">
                        <img 
                            src="/logo-saarthi.svg" 
                            alt="Saarthi" 
                            className="saarthi-logo-image" 
                        />
                        <span className="saarthi-logo-text">Saarthi</span>
                    </Link>
                </div>

                {/* Navigation - TEMP: show only grocery links as top-level items */}
                <div className="saarthi-nav-wrapper">

                    <div className="saarthi-nav-item">
                        <Link to="/highgarden/favorites" className={`saarthi-nav-link ${isActive('/highgarden/favorites') ? 'active' : ''}`} title="Favorites">
                            <span className="material-icons saarthi-nav-icon">favorite</span>
                            <span className="saarthi-nav-text">Favorite Products</span>
                        </Link>
                    </div>

                    <div className="saarthi-nav-item">
                        <Link to="/highgarden/offers" className={`saarthi-nav-link ${isActive('/highgarden/offers') ? 'active' : ''}`} title="Offers">
                            <span className="material-icons saarthi-nav-icon">local_offer</span>
                            <span className="saarthi-nav-text">Offers</span>
                        </Link>
                    </div>

                    <div className="saarthi-nav-item">
                        <Link to="/highgarden/grocery_lists" className={`saarthi-nav-link ${isActive('/highgarden/grocery_lists') ? 'active' : ''}`} title="Grocery Lists">
                            <span className="material-icons saarthi-nav-icon">list_alt</span>
                            <span className="saarthi-nav-text">Grocery Lists</span>
                        </Link>
                    </div>

                    <div className="saarthi-nav-item">
                        <Link to="/highgarden/analytics" className={`saarthi-nav-link ${isActive('/highgarden/analytics') ? 'active' : ''}`} title="Analytics">
                            <span className="material-icons saarthi-nav-icon">insights</span>
                            <span className="saarthi-nav-text">Analytics</span>
                        </Link>
                    </div>
                </div>

                {/* User Profile */}
                <div className="saarthi-user-profile">
                    <div className={`saarthi-dropdown ${activeDropdown === 'user' ? 'active' : ''}`}>
                        <div 
                            className="saarthi-user-initials" 
                            onClick={() => toggleDropdown('user')}
                            title="User Menu"
                        >
                            {getUserInitials()}
                        </div>
                        <div className="saarthi-user-popup">
                            <Link to="/account" className="saarthi-dropdown-item">
                                <span className="material-icons">person</span>
                                {userInfo?.username || 'Profile'}
                            </Link>
                            <div 
                                className="saarthi-dropdown-item logout" 
                                onClick={handleLogout}
                                style={{ cursor: 'pointer' }}
                            >
                                <span className="material-icons">logout</span>
                                Logout
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default LoggedInNavBar;